import React from "react";
import {
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { SchoolAttendanceRecordDetailQuery, Student, StudentNameAndImageFragment } from "../../../../Utilities";
import { StudentFormat } from "../../../Format";
import { SchoolAttendanceRecordStudentDateForm } from "../../../Forms";
import { StudentIcon } from "../../Student";
import styles from "./styles.module.css";

export type SchoolAttendanceRecordStudentDateModalProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	date: string;
	schoolAttendanceRecord: SchoolAttendanceRecordDetailQuery["schoolAttendanceRecord"] | null | undefined;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function SchoolAttendanceRecordStudentDateModal({
	student,
	schoolAttendanceRecord,
	date,
	ifRef,
	onClose,
	className,
	style,
}: SchoolAttendanceRecordStudentDateModalProps) {
	const formNameSuffix = date + "." + student.id;

	const detailFormStateArgs = React.useMemo(
		() => ({
			editMode: EditMode.WriteOnly,
		}),
		[],
	);

	const createFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				studentID: student.id,
				attendanceDate: date,
			},
		}),
		[student.id, date],
	);

	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["student-attendance-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<StudentIcon student={student} size={48} />
					<Heading.H2 noMargin>
						<TruncatedText>{StudentFormat.name(student)}</TruncatedText>
					</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<Conditional>
					<If condition={!!schoolAttendanceRecord}>
						{!!schoolAttendanceRecord && (
							<SchoolAttendanceRecordStudentDateForm.ControlledDetail
								schoolAttendanceRecord={schoolAttendanceRecord}
								formStateArgs={detailFormStateArgs}
								formNameSuffix={formNameSuffix}
								onSuccess={onSuccess}
								style={{ height: "100%", display: "flex", flexDirection: "column" }}
							/>
						)}
					</If>
					<Else>
						<SchoolAttendanceRecordStudentDateForm.ControlledCreate
							formStateArgs={createFormStateArgs}
							formNameSuffix={formNameSuffix}
							onSuccess={onSuccess}
							style={{ height: "100%", display: "flex", flexDirection: "column" }}
						/>
					</Else>
				</Conditional>
			</Modal.Body>
		</Modal>
	);
}
